import type { RuntimeSettings } from 'gql/__generated__/graphql'

import { CMIElement } from './types'

export const DRIVER_VERSION = '1.2.1' // v1.2.1: closeWindow tears down iframe; top.close + self.close (no extra UI)

export type ScormRuntimeVersion = '1.2' | '2004' | 'none'

// --- CMI Element Configuration ---

export type CmiSaveType = 'suspend' | 'force' | 'test'

export type CmiElementConfig = {
  saveType: CmiSaveType
  restore?: boolean
  initRead?: boolean
  readRule?: (data: string) => string
  default: string
  scormVersion?: ScormRuntimeVersion
  /**
   * Priority when flushing to LMS. Lower = sent first.
   * Elements without flushPriority get 0 (sent first).
   *
   * CRITICAL for WebTutor: success_status (20) MUST be sent BEFORE completion_status (30)
   * because WebTutor closes session immediately after receiving 'completed'
   */
  flushPriority?: number
}

/**
 * Configuration for CMI elements - controls how each element is saved, restored, and flushed.
 *
 * saveType:
 * - 'force': Triggers immediate flush + commit (used for status changes)
 * - 'test': Queued for batch send (used for score fields)
 * - 'suspend': Debounced save (used for suspend_data, location, progress)
 *
 * flushPriority:
 * - Lower number = sent first when flushing batch to LMS
 * - Critical for WebTutor: success_status must arrive BEFORE completion_status
 */
export const CMI_CONFIG: Partial<Record<CMIElement, CmiElementConfig>> = {
  'cmi.completion_status': {
    initRead: true,
    saveType: 'force',
    default: 'unknown',
    flushPriority: 30, // Must be LAST among status fields
  },
  'cmi.success_status': {
    initRead: true,
    saveType: 'test',
    default: 'unknown',
    flushPriority: 20,
  },
  'cmi.score.raw': {
    saveType: 'test',
    default: '0',
    flushPriority: 12,
  },
  'cmi.score.scaled': {
    saveType: 'test',
    default: '0',
    flushPriority: 13,
  },
  'cmi.score.min': {
    saveType: 'test',
    default: '0',
    flushPriority: 10,
  },
  'cmi.score.max': {
    saveType: 'test',
    default: '0',
    flushPriority: 11,
  },
  'cmi.location': {
    initRead: true,
    saveType: 'suspend',
    default: '',
  },
  'cmi.suspend_data': {
    initRead: true,
    saveType: 'suspend',
    default: '',
  },
  'cmi.progress_measure': {
    initRead: true,
    saveType: 'suspend',
    default: '0',
  },
  'cmi.exit': {
    saveType: 'force',
    default: '',
    flushPriority: 40,
  },
  // SCORM 2004 learner info (read-only)
  'cmi.learner_id': {
    initRead: true,
    saveType: 'force',
    default: '',
    scormVersion: '2004',
  },
  'cmi.learner_name': {
    initRead: true,
    saveType: 'force',
    default: '',
    scormVersion: '2004',
  },
  // SCORM 1.2 learner info (read-only)
  'cmi.core.student_id': {
    initRead: true,
    saveType: 'force',
    default: '',
    scormVersion: '1.2',
  },
  'cmi.core.student_name': {
    initRead: true,
    saveType: 'force',
    default: '',
    scormVersion: '1.2',
  },
}

export type ScormMethod =
  | 'initialize'
  | 'terminate'
  | 'getvalue'
  | 'setvalue'
  | 'setBatch' // v1.1.0: Batch set multiple values
  | 'flush' // v1.1.0: Flush pending values to LMS
  | 'commit'
  | 'getLastError'
  | 'getErrorString'
  | 'getDiagnostic'
  | 'getVersion'
  | 'closeWindow' // Explicit window close command
  | 'getDriverVersion' // Get driver version for compatibility check
  | 'courseReady' // Phase B.2: fire-and-forget signal from course → router that UI is mounted

// Message types for cross-domain communication
export interface IScormRequest {
  type: 'scorm_call'
  id: string
  method: ScormMethod
  channelId?: string // v1.2.0: Channel ID for isolation when nested courses are present

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  params?: any
}

export interface IScormResponse {
  type: 'scorm_response'
  id: string
  result: string
  error?: string
  channelId?: string // v1.2.0: Channel ID for isolation when nested courses are present
}

export type ISaveSettings = Pick<RuntimeSettings, 'suspendDataInterval' | 'suspendDataType'>

export interface ISetBatchItem {
  element: CMIElement
  value: string
}

export interface ICrossDomainConfig {
  targetOrigin?: string // Optional origin restriction
  timeout?: number // Response timeout in ms
  debug?: boolean // Enable debug logging
  // Phase B.2 router hooks — launcher uses these to forward
  // course state transitions to the portal parent without
  // tapping into SCORM internals.
  onSetValue?: (element: CMIElement, value: string) => void
  onSessionStatus?: (status: 'initialized' | 'terminated') => void
  onCourseReady?: () => void
}

export const DEFAULT_CONFIG: ICrossDomainConfig = {
  targetOrigin: '*',
  timeout: 5000,
  debug: true,
}
